import { Product } from 'shared/types/product';
import { isHiddenCategory, isWoodPostCableCategory } from 'helpers/catalog/categoryOverrides';
import { isStorefrontCategory } from 'helpers/catalog/storefrontCategory';

// Handrail profiles are merchandised under railing systems too (6000 Handrail sits in Signature Rod
// Railing), but their home is the Handrail section. Only the profile listings win this way: glass top
// caps and brackets keep their deeper railing-system category.
const HANDRAIL_HOME_URLS = ['/handrail/modern-wood-handrail', '/handrail/metal-handrail'];
const isHandrailHome = (url?: string) => HANDRAIL_HOME_URLS.includes((url ?? '').replace(/\/$/, ''));

/**
 * The product's most specific category: the deepest one assigned, ties broken by the highest
 * `orderHint`. Product categories carry `depth` but never `descendants` (only the nav-tree builder
 * fills those in), and `orderHint` only orders siblings, so depth has to decide first — otherwise a
 * broad parent or merchandising category with a higher hint wins across levels. A handrail profile
 * listing beats both, and a hidden category is only used when the product has nothing else.
 */
// The catalog is shared with StairSupplies, so a product also sits in that store's categories
// (DriveTite is in its "Modern Railing" tree, deeper than ours). Only this storefront's categories
// are candidates; with no ancestry data on any of them, all are.
export const getProductParentCategory = (product: Product) => {
  const all = product.categories ?? [];
  const storefront = all.some(isStorefrontCategory) ? all.filter(isStorefrontCategory) : all;
  const visible = storefront.filter((category) => !isHiddenCategory(category));
  const candidates = visible.length ? visible : storefront;
  if (!candidates.length) return undefined;
  // DriveTite products and tools (some also sit in Rod Railing) belong to Wood Post Cable Railing first.
  const woodPost = candidates.filter((category) => isWoodPostCableCategory(category._url));
  const handrailHome = candidates.filter((category) => isHandrailHome(category._url));
  const preferred = woodPost.length ? woodPost : handrailHome;

  return (preferred.length ? preferred : candidates).reduce((best, category) => {
    const depthDifference = (category.depth ?? 0) - (best.depth ?? 0);
    if (depthDifference !== 0) return depthDifference > 0 ? category : best;
    return (category.orderHint ?? 0) > (best.orderHint ?? 0) ? category : best;
  });
};
